import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UploadSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useApi, errMsg, nzDate, nzDateTime } from "@/lib/api";
import { Btn, Field, Modal, Section, Empty, Pill, Sel, useLoad } from "@/components/app/common";
import { WeightChart, StockSummary, AnimalTable, CommentThread } from "@/components/grazing/shared";
import { WeightImport } from "@/components/grazing/WeightImport";

function NewMob({ clients, onClose, onDone }) {
  const api = useApi();
  const [f, setF] = useState({ client_id: clients[0]?.id || "", name: "", arrival_date: "" });
  const save = async () => { try { const m = await api.post("/grazing/mobs", f); toast.success("Mob created"); onDone(m); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Modal open title="New mob" onClose={onClose} testid="new-mob-modal">
      <div className="space-y-3">
        <Field label="Grazier"><Sel value={f.client_id} onChange={(v) => setF({ ...f, client_id: v })} options={clients.map((c) => ({ value: c.id, label: c.name }))} testid="new-mob-client" /></Field>
        <Field label="Mob name"><input className="field-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. R2 heifers 2026" data-testid="new-mob-name" /></Field>
        <Field label="Arrival date"><input type="date" className="field-input" value={f.arrival_date} onChange={(e) => setF({ ...f, arrival_date: e.target.value })} data-testid="new-mob-arrival" /></Field>
        <Btn onClick={save} disabled={!f.client_id || !f.name.trim()} data-testid="new-mob-save">Create mob</Btn>
      </div>
    </Modal>
  );
}

function AddAnimal({ mob, onClose, onDone }) {
  const api = useApi();
  const [f, setF] = useState({ tag: "", eid: "", nait: "", breed: "" });
  const save = async () => { try { await api.post("/grazing/animals", { ...f, mob_id: mob.id }); toast.success("Animal added"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Modal open title={`Add animal to ${mob.name}`} onClose={onClose} testid="add-animal-modal">
      <div className="grid sm:grid-cols-2 gap-3">
        {[["tag", "Visual tag"], ["eid", "EID"], ["nait", "NAIT number"], ["breed", "Breed"]].map(([k, l]) => <Field key={k} label={l}><input className="field-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`add-animal-${k}`} /></Field>)}
      </div>
      <Btn className="mt-4" onClick={save} disabled={!f.tag.trim() && !f.eid.trim()} data-testid="add-animal-save">Add animal</Btn>
    </Modal>
  );
}

function Uploads({ mobId, manager, tick, onChange }) {
  const api = useApi();
  const [ups, reload] = useLoad(() => api.get(`/grazing/weights/uploads?mob_id=${mobId}`), [mobId, tick]);
  const undo = async (u) => { if (!window.confirm(`Undo "${u.filename}"? Its ${u.weights} weights and any animals it added (with no other records) are removed.`)) return; try { await api.del(`/grazing/weights/uploads/${u.id}`); toast.success("Import undone"); reload(); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  if (!ups?.length) return null;
  return (
    <Section title="Weight uploads" testid="upload-history">
      <ul className="divide-y divide-slate-100 text-sm">{ups.map((u) => (
        <li key={u.id} className="py-2 flex flex-wrap justify-between gap-2" data-testid={`upload-${u.id}`}>
          <span><span className="font-medium">{u.filename}</span> · {u.weights} weights{u.new_animals ? ` · ${u.new_animals} new animals` : ""} · {u.dates.map(nzDate).join(", ")}<span className="block text-xs text-slate-500">{u.by_name} · {nzDateTime(u.at)}</span></span>
          {manager && <button className="text-xs underline inline-flex items-center gap-1" onClick={() => undo(u)} data-testid={`upload-undo-${u.id}`}><ArrowCounterClockwise size={14} />Undo</button>}
        </li>))}</ul>
    </Section>
  );
}

function MobDetail({ mobId, manager, onChanged }) {
  const api = useApi();
  const [tick, setTick] = useState(0);
  const [d, reload] = useLoad(() => api.get(`/grazing/mobs/${mobId}/stock`), [mobId, tick]);
  const [modal, setModal] = useState(null);
  const refresh = () => { setTick((t) => t + 1); onChanged(); };
  if (!d) return <p className="text-sm">Loading…</p>;
  const { mob } = d;
  const setStatus = async (a, status) => { try { await api.patch(`/grazing/animals/${a.id}`, { status }); reload(); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const mobStatus = async (status) => { if (status === "departed" && !window.confirm(`Mark ${mob.name} as departed? All its active animals are marked departed.`)) return; try { await api.patch(`/grazing/mobs/${mob.id}`, { status }); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="mob-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="text-2xl font-bold" data-testid="mob-detail-name">{mob.name}</h2>
          <p className="text-sm text-slate-600">{mob.client_name} · arrived {nzDate(mob.arrival_date)}{mob.status === "departed" && ` · departed ${nzDate(mob.departure_date)}`} <Pill tone={mob.status === "active" ? "green" : "slate"}>{mob.status}</Pill></p></div>
        {manager && <div className="flex flex-wrap gap-2">
          {mob.status === "active" && <Btn onClick={() => setModal("import")} data-testid="upload-weights-button"><UploadSimple size={16} />Upload weights CSV</Btn>}
          {mob.status === "active" && <Btn variant="outline" onClick={() => setModal("animal")} data-testid="add-animal-button"><Plus size={16} />Animal</Btn>}
          <Btn variant="ghost" onClick={() => mobStatus(mob.status === "active" ? "departed" : "active")} data-testid="mob-status-button">{mob.status === "active" ? "Mark departed" : "Reactivate"}</Btn></div>}
      </div>
      <StockSummary summary={d.summary} />
      <Section title="Average weight & daily gain" testid="mob-weight-section"><WeightChart sessions={d.sessions} /></Section>
      <Section title="Animals" testid="mob-animals"><AnimalTable base="/grazing" animals={d.animals} actions={manager ? (a) => (
        <select className="text-xs border border-slate-200 rounded px-1 py-1" value={a.status} onChange={(e) => setStatus(a, e.target.value)} data-testid={`animal-status-${a.id}`}>
          {["active", "departed", "sold", "dead"].map((s) => <option key={s}>{s}</option>)}</select>) : undefined} /></Section>
      <Uploads mobId={mob.id} manager={manager} tick={tick} onChange={refresh} />
      <CommentThread base="/grazing" mobId={mob.id} animals={d.animals} />
      {modal === "import" && <WeightImport mob={mob} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal === "animal" && <AddAnimal mob={mob} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
    </div>
  );
}

export function MobsTab({ farmId, clients, manager }) {
  const api = useApi();
  const [mobs, reload] = useLoad(() => api.get(`/grazing/mobs?farm_id=${farmId}`), [farmId]);
  const [params] = useSearchParams();
  const [sel, setSel] = useState(params.get("mob"));
  const [show, setShow] = useState("active");
  const [creating, setCreating] = useState(false);
  const list = (mobs || []).filter((m) => show === "all" || m.status === show);
  const current = sel || list[0]?.id;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      <div className="space-y-3 min-w-0">
        <div className="flex gap-2"><Sel value={show} onChange={setShow} options={[{ value: "active", label: "Active mobs" }, { value: "departed", label: "Departed" }, { value: "all", label: "All mobs" }]} testid="mob-filter" />
          {manager && <Btn onClick={() => setCreating(true)} disabled={!clients.length} data-testid="new-mob-button" className="shrink-0 px-3"><Plus size={16} /></Btn>}</div>
        {!clients.length && <p className="text-xs text-slate-500">Add a grazier first.</p>}
        {list.length === 0 ? <Empty testid="mobs-empty">No mobs.</Empty> : <ul className="space-y-2" data-testid="mob-list">{list.map((m) => (
          <li key={m.id}><button onClick={() => setSel(m.id)} data-testid={`mob-item-${m.id}`} className={`w-full text-left panel p-3 transition-colors ${current === m.id ? "ring-2 ring-emerald-700" : "hover:bg-slate-50"}`}>
            <p className="font-semibold text-sm">{m.name}</p><p className="text-xs text-slate-500">{m.client_name} · {m.active_animals} active</p></button></li>))}</ul>}
      </div>
      <div className="min-w-0">{current ? <MobDetail key={current} mobId={current} manager={manager} onChanged={reload} /> : <Empty>Select or create a mob.</Empty>}</div>
      {creating && <NewMob clients={clients.filter((c) => !c.archived)} onClose={() => setCreating(false)} onDone={(m) => { setCreating(false); setSel(m.id); setShow("active"); reload(); }} />}
    </div>
  );
}
